"use client";

import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPaperPlaneRight, PiEnvelopeSimple, PiSparkle, PiCheckCircle } from "react-icons/pi";

export default function NewsletterCard({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#6366F1";
    const fontFamily = content.fontFamily || "inherit";

    const features = content.features || [
        "Weekly insights & tips",
        "Exclusive member content",
        "Early access to new features"
    ];

    return (
        <section
            className={`px-6 py-20 bg-gradient-to-br from-gray-50 to-gray-100 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            <div className="max-w-6xl mx-auto">
                <div className="relative overflow-hidden rounded-3xl bg-white shadow-2xl">
                    {/* Decorative Background Elements */}
                    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full blur-3xl opacity-60 -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-100 to-cyan-100 rounded-full blur-3xl opacity-60 translate-y-1/2 -translate-x-1/2" />

                    <div className="relative z-10 grid md:grid-cols-2 gap-8 p-8 md:p-12 lg:p-16">
                        {/* Left Content */}
                        <div className="flex flex-col justify-center">
                            {/* Badge */}
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 text-indigo-600 text-sm font-semibold mb-6 w-fit">
                                <PiSparkle className="w-4 h-4" />
                                <EditableText
                                    value={content.badge || "Join 10,000+ subscribers"}
                                    onChange={(v) => onContentChange?.({ ...content, badge: v })}
                                    as="span"
                                />
                            </div>

                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 tracking-tight mb-6 leading-tight">
                                <EditableText
                                    value={content.title || "Get the latest updates"}
                                    onChange={(v) => onContentChange?.({ ...content, title: v })}
                                />
                            </h2>

                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                <EditableText
                                    value={content.description || "Subscribe to our newsletter and never miss out on exclusive content, expert tips, and special offers."}
                                    onChange={(v) => onContentChange?.({ ...content, description: v })}
                                />
                            </p>

                            {/* Features List */}
                            <div className="space-y-3 mb-8">
                                {features.map((feature: string, idx: number) => (
                                    <div key={idx} className="flex items-center gap-3">
                                        <div
                                            className="w-6 h-6 rounded-full flex items-center justify-center text-white"
                                            style={{ backgroundColor: primaryColor }}
                                        >
                                            <PiCheckCircle className="w-4 h-4" />
                                        </div>
                                        <span className="text-gray-700 font-medium">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right - Form Card */}
                        <div className="flex items-center justify-center">
                            <div
                                className="w-full max-w-md p-8 rounded-2xl shadow-xl border border-gray-100"
                                style={{ backgroundColor: "white" }}
                            >
                                {/* Icon */}
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                                    style={{ backgroundColor: `${primaryColor}15` }}
                                >
                                    <PiEnvelopeSimple className="w-8 h-8" style={{ color: primaryColor }} />
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                                    <EditableText
                                        value={content.formTitle || "Subscribe now"}
                                        onChange={(v) => onContentChange?.({ ...content, formTitle: v })}
                                    />
                                </h3>
                                <p className="text-gray-500 text-center text-sm mb-6">
                                    <EditableText
                                        value={content.formSubtitle || "Get started with your free account today"}
                                        onChange={(v) => onContentChange?.({ ...content, formSubtitle: v })}
                                    />
                                </p>

                                {/* Form */}
                                <div className="space-y-4">
                                    <input
                                        type="text"
                                        placeholder={content.namePlaceholder || "Your name"}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    />
                                    <input
                                        type="email"
                                        placeholder={content.emailPlaceholder || "Your email address"}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    />
                                    {content.buttonLink ? (
                                        <a
                                            href={onContentChange ? undefined : content.buttonLink}
                                            target={content.buttonLink.startsWith('http') && !onContentChange ? "_blank" : undefined}
                                            rel={content.buttonLink.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                                            className="w-full px-4 py-4 rounded-xl text-white font-bold transition-all hover:opacity-90 hover:shadow-lg flex items-center justify-center gap-2 group"
                                            style={{ backgroundColor: primaryColor }}
                                            onClick={(e) => {
                                                if (onContentChange) e.preventDefault();
                                            }}
                                        >
                                            <EditableText
                                                value={content.buttonText || "Subscribe"}
                                                onChange={(v) => onContentChange?.({ ...content, buttonText: v })}
                                                as="span"
                                            />
                                            <PiPaperPlaneRight className="text-xl transition-transform group-hover:translate-x-1" />
                                        </a>
                                    ) : (
                                        <button
                                            className="w-full py-4 rounded-xl font-bold text-white transition-all hover:opacity-90 hover:shadow-lg flex items-center justify-center gap-2 group"
                                            style={{ backgroundColor: primaryColor }}
                                        >
                                            <EditableText
                                                value={content.buttonText || "Subscribe"}
                                                onChange={(v) => onContentChange?.({ ...content, buttonText: v })}
                                            />
                                            <PiPaperPlaneRight className="text-xl transition-transform group-hover:translate-x-1" />
                                        </button>
                                    )}
                                </div>

                                <p className="mt-4 text-xs text-gray-400 text-center">
                                    <EditableText
                                        value={content.disclaimer || "By subscribing, you agree to our Privacy Policy."}
                                        onChange={(v) => onContentChange?.({ ...content, disclaimer: v })}
                                    />
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
